import React, { useState, useEffect } from 'react';
import { compressImage } from '../../helpers/compressor';
import AddProfileImage from '../../assets/icons/AddProfileImage';
import Input from '../../ui/Input';
import Info from '../../assets/icons/Info';
import { uploader } from '../../helpers/uploader';
import { toast } from 'sonner';
import { useLoadingState } from '../../hooks/useLoader';
import Loader from '../../ui/Loader';
import Button from '../../ui/Button';
import Feedbackclose from '../../assets/icons/Feedbackclose';

const UploadLogo = ({
    fieldName,
    errorField,
    errors,
    isUploadEnable = true,
    autoUpload = false,
    setError,
    clearErrors,
    outerPadding = 'p-6',
    size = 9.5
}) => {
    const { loading, startLoading, stopLoading } = useLoadingState();
    const [uploadPicture, setUploadPicture] = useState({
        image: null,
        imageURL: '',
    });

    useEffect(() => {
        // Clean up the URL when component unmounts or when image changes
        return () => {
            if (uploadPicture.imageURL) {
                URL.revokeObjectURL(uploadPicture.imageURL);
            }
        };
    }, [uploadPicture.imageURL]);

    useEffect(() => {
        // When isUploadEnable is false, directly update the field after setting the picture
        if (!isUploadEnable && uploadPicture.image && !autoUpload) {
            fieldName.onChange(uploadPicture.image);
        }
    }, [isUploadEnable, uploadPicture.image, fieldName, autoUpload]);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];

        if (file) {
            if (autoUpload) startLoading();
            try {
                const processedFile = await compressImage(file, size);
                const imageURL = URL.createObjectURL(processedFile);

                // Clear previous errors if any
                if (clearErrors) clearErrors(errorField);
                if (setError) setError(errorField, null);

                setUploadPicture({
                    image: processedFile,
                    imageURL: imageURL,
                });

                if (autoUpload) {
                    const res = await uploader(processedFile);
                    if (res.files.length) {
                        fieldName.onChange(res.files[0]);
                        toast.success('Image successfully uploaded.');
                    } else {
                        toast.error('Sorry, unable to upload image.');
                    }
                    stopLoading();
                } else if (!isUploadEnable) {
                    fieldName.onChange(processedFile); // Only auto-change if uploads are disabled
                }
            } catch (error) {
                console.error("Image processing failed", error);

                const imageURL = URL.createObjectURL(file);
                // Fallback to original file
                if (clearErrors) clearErrors(errorField);
                if (setError) setError(errorField, null);

                setUploadPicture({
                    image: file,
                    imageURL: imageURL,
                });

                if (autoUpload) {
                    // Try uploading original if compression failed
                    try {
                        const res = await uploader(file);
                        if (res.files.length) {
                            fieldName.onChange(res.files[0]);
                            toast.success('Image successfully uploaded.');
                        } else {
                            toast.error('Sorry, unable to upload image.');
                        }
                    } catch (uploadError) {
                        toast.error('Upload failed.');
                    }
                    stopLoading();
                } else if (!isUploadEnable) {
                    fieldName.onChange(file);
                }
            }
        } else {
            setUploadPicture({ image: null, imageURL: '' });
            fieldName.onChange('');
            if (clearErrors) clearErrors(errorField);
        }
    };

    const handleUploadClick = async () => {
        startLoading();
        const res = await uploader(uploadPicture.image);
        if (res.files.length) {
            fieldName.onChange(res.files[0]); // Update field with the uploaded file
            toast.success('Logo uploaded. ');
        } else {
            toast.error('Sorry unable to upload logo');
        }
        stopLoading();
    };

    return (
        <div>
            <div className={`${outerPadding} bg-grayBgNormal flex flex-col text-textSecondary items-center shadow-sm rounded-md`}>
                <div className="border relative flex flex-col gap-2 border-borderUnderline border-dashed w-full p-6 rounded-md">
                    {uploadPicture.imageURL && (
                        <Button
                            onClick={() => {
                                fieldName.onChange('');
                                setUploadPicture({ image: '', imageURL: '' });
                            }}
                            className="border-0 absolute top-3 right-4"
                            variant="tertiary"
                            size="circle"
                            disabled={loading}
                        >
                            <Feedbackclose />
                        </Button>
                    )}

                    {loading ? (
                        <div className="flex flex-col gap-2 items-center justify-center p-4">
                            <Loader />
                            <p className="text-sm font-medium animate-pulse">Compressing & Uploading...</p>
                        </div>
                    ) : (
                        <>
                            {!uploadPicture.imageURL && (
                                <>
                                    <label className="flex flex-col gap-2 items-center" htmlFor="avatar">
                                        <AddProfileImage />
                                        <p className='text-textSecondary' >Click to choose file</p>
                                    </label>
                                    <Input {...fieldName} onChange={handleFileChange} type="file" />
                                </>
                            )}
                            {uploadPicture.imageURL && (
                                <img
                                    className="w-20 h-20 rounded-md object-center object-cover"
                                    src={uploadPicture.imageURL}
                                    alt="profile preview"
                                    onError={() => setUploadPicture((prev) => ({ ...prev, imageURL: '' }))}
                                />
                            )}
                            {!uploadPicture.imageURL && (
                                <div className="flex gap-2 justify-center items-center">
                                    <Info />
                                    <p className="font-semibold text-textSecondary">Max Ideal file size: {size} MB; Ideal dimensions: 75x75 px</p>
                                </div>
                            )}
                        </>
                    )}

                    {isUploadEnable && !autoUpload && uploadPicture.image && !loading && (
                        <Button
                            className={`mt-4 ${fieldName.value ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            variant="tertiary"
                            type="button"
                            disabled={fieldName.value}
                            onClick={handleUploadClick}
                        >
                            {fieldName.value ? 'Uploaded' : loading ? <Loader /> : 'Upload'}
                        </Button>
                    )}
                </div>
            </div>

            {errors[errorField]?.message && <p className="text-foundryRed mt-1">{errors[errorField]?.message}</p>}
        </div>
    );
};

export default UploadLogo;
